
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Share2, Copy, Check, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/hooks/use-toast';

interface ShareLeadsDialogProps {
  selectedLeadIds: Set<string>;
}

const generateToken = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 24; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
};

const ShareLeadsDialog: React.FC<ShareLeadsDialogProps> = ({ selectedLeadIds }) => {
  const { user, profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expiry, setExpiry] = useState<string>('never');
  const [shareName, setShareName] = useState('');

  const handleGenerate = async () => {
    if (!user || selectedLeadIds.size === 0) return;
    setIsGenerating(true);

    try {
      const token = generateToken();
      const expiresAt = expiry === '7d'
        ? new Date(Date.now() + 7 * 86400000).toISOString()
        : expiry === '30d'
          ? new Date(Date.now() + 30 * 86400000).toISOString()
          : null;

      const { error } = await supabase.from('lead_shares').insert({
        token,
        name: shareName.trim() || null,
        lead_ids: Array.from(selectedLeadIds),
        created_by: user.id,
        company_id: profile?.company_id ?? null,
        expires_at: expiresAt,
      } as any);

      if (error) throw error;

      const link = `https://leads.mikai.ai/shared/${token}`;
      setShareLink(link);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareLink);
    setCopied(true);
    toast({ title: 'Link copied to clipboard' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setShareLink('');
      setCopied(false);
      setShareName('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm gap-1">
          <Share2 className="h-4 w-4" />
          <span className="hidden sm:inline">Share</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share {selectedLeadIds.size} Lead{selectedLeadIds.size > 1 ? 's' : ''}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">
            Generate a link that any logged-in user can open to view these leads (read-only).
          </p>

          {!shareLink ? (
            <>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Share name</label>
                <Input
                  value={shareName}
                  onChange={(e) => setShareName(e.target.value)}
                  placeholder="e.g. Q1 Restaurant Leads"
                  className="h-9"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Link expiry</label>
                <Select value={expiry} onValueChange={setExpiry}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">Never expires</SelectItem>
                    <SelectItem value="7d">Expires in 7 days</SelectItem>
                    <SelectItem value="30d">Expires in 30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleGenerate} disabled={isGenerating || !shareName.trim()} className="w-full">
                {isGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
                Generate Link
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input value={shareLink} readOnly className="text-xs" />
                <Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0">
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {expiry === 'never' ? 'This link never expires.' : `This link expires in ${expiry === '7d' ? '7' : '30'} days.`}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareLeadsDialog;
